import { inject, Injectable } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { FeatureFlagService } from '@ddv/entitlements';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import { ManagerService } from '@ddv/layout';
import { AppWidgetState, Client, Fund, NamedQuery, VisualizationType, WidgetSettings, WidgetState } from '@ddv/models';
import { ClientsService, FundsService } from '@ddv/reference-data';
import { GridEvent } from '@ddv/visualizations';
import { map, Observable, Subject, switchMap } from 'rxjs';

@Injectable()
export class WidgetService {
    private readonly clientsService = inject(ClientsService);
    private readonly currentStateService = inject(CurrentStateService);
    private readonly ddvApiService = inject<ApiExecutorService>(ApiServices.ddvApi);
    private readonly ddvMWService = inject<ApiExecutorService>(ApiServices.ddvMW);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly fundsService = inject(FundsService);
    private readonly managerService = inject(ManagerService);

    public readonly gridStateObs: Observable<GridEvent>;

    private readonly gridStateEvent = new Subject<GridEvent>();
    private funds: Fund[] = [];
    private clients: Client[] = [];
    private isMultiClient = false;
    private clientCode = '';

    constructor() {
        this.gridStateObs = this.gridStateEvent.asObservable();

        this.currentStateService.clientCode$.subscribe((clientCode) => this.clientCode = clientCode);

        this.currentStateService.isMultiClient$
            .pipe(switchMap((isMultiClient) => {
                this.isMultiClient = isMultiClient;
                if (this.isMultiClient) {
                    return this.clientsService.clients();
                }

                return this.fundsService.funds();
            }))
            .subscribe((data: Fund[] | string[]) => {
                if (data.some((datum) => datum instanceof Fund)) {
                    this.funds = data as Fund[];
                } else {
                    this.clients = (data as string[]).map((clientCode) => {
                        return { clientId: clientCode, clientName: clientCode };
                    });
                }
            });
    }

    getWidgetPreferences(widgetId: number, clientCode: string): Observable<AppWidgetState> {
        return this.featureFlagService.isFlagEnabled('use-aws-for-widgets', clientCode)
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        return this.ddvApiService.invokeServiceWithParams<Partial<AppWidgetState>>(clientCode, `widgets/${widgetId}`);
                    }

                    return this.ddvMWService.invokeServiceWithParams<Partial<AppWidgetState>>(clientCode, `widgets/${widgetId}`);
                }),
                map((w) => {
                    if (this.isMultiClient) {
                        return new AppWidgetState(w, [], this.clients);
                    }
                    return new AppWidgetState(w, this.funds);
                }),
            );
    }

    getWidgetSettings(widget: AppWidgetState): WidgetSettings {
        const { widgetSettings } = widget;
        const vizType = widget.visualizationConfigs[0]?.visualizationType;
        /* eslint-disable eqeqeq */
        let isActiveDate = widgetSettings?.isActiveDate !== null ? widgetSettings?.isActiveDate : widget.isActiveDate;
        if (isActiveDate === null) {
        /* eslint-enable eqeqeq */
            isActiveDate = this.getDefaultIsActiveDate(vizType);
        }

        if (this.isMultiClient) {
            return {
                isActiveDate: !!isActiveDate,
                isSubscribedToDashboardFilters: true,
            };
        }

        return {
            isActiveDate: !!isActiveDate,
            isSubscribedToDashboardFilters: widget.isSubscribedToDashboardFilters,
        };
    }

    getDefaultWidgetState(widgetId?: number): AppWidgetState {
        let widgetState: WidgetState | undefined = {};
        if (widgetId) {
            widgetState = this.managerService.getWidgetState(widgetId);
        }

        return new AppWidgetState({
            id: undefined,
            selector: '',
            name: '',
            visualizationConfigs: [],
            namedQuery: { id: '', name: '' } as NamedQuery,
            isActiveDate: true,
            sortTableBy: 'SLICER',
            tableSortDirection: 'ASC',
            isSubscribedToDashboardFilters: true,
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            maximized: false,
            widgetFilters: { funds: [], filters: [] },
            clientCode: this.clientCode,
            ...widgetState,
        });
    }

    updateIsMasterOnWidgetExtraParams(widgetId: number, isMaster: boolean): void {
        const widgetParams = this.managerService.getWidgetPreferences(widgetId);
        if (widgetParams) {
            widgetParams.isMaster = isMaster;
            this.managerService.setWidgetExtraPreferences(widgetId, widgetParams);
        }
    }

    publishGridStateUpdated(gridState: GridEvent): void {
        this.gridStateEvent.next(gridState);
    }

    getDefaultIsActiveDate(vizType: VisualizationType | undefined): boolean {
        switch (vizType) {
            case 'PIE_CHART':
            case 'DONUT_CHART':
            case 'HORIZONTAL_BAR_CHART':
            case 'VERTICAL_BAR_CHART':
            case 'HORIZONTAL_STACKED_BAR_CHART':
            case 'VERTICAL_STACKED_BAR_CHART':
            case 'VERTICAL_MIRRORED_BAR_CHART':
                return true;
            default:
                return false;
        }
    }
}
